import React, { FC, ReactElement, useCallback, useContext, useMemo } from 'react'
import { LinearProgress } from '@material-ui/core'
import { HighlightInformationDTO } from '../../../../React/InsightsHub/Types/Types'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../../../React/Common/Contexts/LanguageContext'
import { InsightsDashboardLanguage } from '../../../../../i18n'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

type Props = {
    highlightInfo: Nullable<HighlightInformationDTO>
}

type GraphEntry = {
    label: string
    value: number
}

const normalise = (value: number, max: number): number => (value / max) * 100

export const InsightsHighlightInfo: FC<Props> = ({ highlightInfo }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const mostCalledNumbers: GraphEntry[] = useMemo(
        (): GraphEntry[] =>
            Object.entries(highlightInfo?.MostCalledInboundNumbers ?? {})
                ?.map(
                    ([label, value]: [string, number]): GraphEntry => ({
                        label,
                        value
                    })
                )
                ?.filter(isVarNotEmpty),
        [highlightInfo]
    )

    const mostAnswered: GraphEntry[] = useMemo(
        (): GraphEntry[] =>
            Object.entries(highlightInfo?.MostAnsweredCallsUser ?? {})
                ?.map(
                    ([label, value]: [string, number]): GraphEntry => ({
                        label,
                        value
                    })
                )
                ?.filter(isVarNotEmpty),
        [highlightInfo]
    )

    const mostCalledMaxValue: number = useMemo(
        (): number =>
            mostCalledNumbers.reduce(
                (acc: number, curr: GraphEntry): number => (curr.value > acc ? curr.value : acc),
                0
            ),
        [mostCalledNumbers]
    )

    const mostAnsweredMaxValue: number = useMemo(
        (): number =>
            mostAnswered.reduce((acc: number, curr: GraphEntry): number => (curr.value > acc ? curr.value : acc), 0),
        [mostAnswered]
    )

    const mostCalledNumber: Definable<string> = useMemo(
        (): Definable<string> => mostCalledNumbers?.[0]?.label,
        [mostCalledNumbers]
    )

    const mostAnsweredNumber: Definable<string> = useMemo(
        (): Definable<string> => mostAnswered?.[0]?.label,
        [mostAnswered]
    )

    const createGraphEntry = useCallback(
        (name: string, maxValue: number) =>
            (graphEntry: GraphEntry, index: number): ReactElement => (
                <div className='idiihi-graph-entry' key={`${name}-${index}`}>
                    <div className='idiihige-label'>
                        <span>{graphEntry.label}</span>
                        <strong>{graphEntry.value}</strong>
                    </div>
                    <LinearProgress
                        classes={{ determinate: 'idiihige-progress', bar1Determinate: 'idiihige-progress-bar' }}
                        variant='determinate'
                        value={normalise(graphEntry.value, maxValue)}
                    />
                </div>
            ),
        []
    )

    return (
        <>
            <div className='idis-stat idii-highlight-info idiihi-most-called'>
                <div className='idiihi-title'>
                    {language.get(InsightsDashboardLanguage.InsightsDashboard_MostCalledInboundNumber)}
                </div>
                <div className='idiihi-subtitle'>
                    {language.get(InsightsDashboardLanguage.InsightsDashboard_OverLast7Days)}
                </div>
                <div className='idiihi-value'>{mostCalledNumber}</div>
                <div className='idiihi-graph'>
                    <div className='idiihig-title'>
                        {language.get(InsightsDashboardLanguage.InsightDashboard_CalledNumber)}
                    </div>
                    <div className='idiihi-graph-entries'>
                        {mostCalledNumbers.map(createGraphEntry('most-called-number', mostCalledMaxValue))}
                    </div>
                </div>
            </div>
            <div className='idis-stat idii-highlight-info idiihi-most-answered'>
                <div className='idiihi-title'>
                    {language.get(InsightsDashboardLanguage.InsightsDashboard_MostAnsweredCalls)}
                </div>
                <div className='idiihi-subtitle'>
                    {language.get(InsightsDashboardLanguage.InsightsDashboard_OverLast7Days)}
                </div>
                <div className='idiihi-value'>{mostAnsweredNumber}</div>
                <div className='idiihi-graph'>
                    <div className='idiihig-title'>
                        {language.get(InsightsDashboardLanguage.InsightsDashboard_User)}
                    </div>
                    <div className='idiihi-graph-entries'>
                        {mostAnswered.map(createGraphEntry('most-answered', mostAnsweredMaxValue))}
                    </div>
                </div>
            </div>
        </>
    )
}
